import type { FC } from 'react'

import { Carousel } from 'components/Carousel'
import { SectionTitle } from 'components/common/Titles'
import { NextLink } from 'components/NextLink'
import { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useProductSearch } from 'services/Search/hooks/use-product-search'
import { SortKey, SortOrder, sortToQueryValue } from 'services/Search/utils/sort'
import styled from 'styled-components'
import { useIsClientSide } from 'utils/hooks/use-is-client-side'
import { TrackingContextProvider } from 'utils/tracking/components/TrackingContextProvider'
import { useProductsImpressionTracking } from 'utils/tracking/hooks/use-products-impression-tracking'
import { TrackingContext } from 'utils/tracking/interfaces/data-layer-events'
import { StyledProductTitleWithCTA } from 'views/contentful/components/contentful-section/FeaturedProducts'

const LIST_NAME_PREFIX = 'top-products-upsell'

const SORT_BY_SCORE = sortToQueryValue([SortKey.Score, SortOrder.Desc])

interface Props {
  categoryName: string | null
  categorySlug: string | null
  className?: string
  ean: string | null
  includeAlcohol?: boolean
}

const PLACEHOLDERS: string[] = Array(12).fill('')

const _TopProductsUpsell: FC<Props> = ({
  categoryName,
  categorySlug,
  className,
  ean,
  includeAlcohol,
}) => {
  const { t } = useTranslation()
  const ref = useRef<HTMLElement>(null)
  const isClientSide = useIsClientSide()

  const { called, loading, data, error } = useProductSearch({
    context: TrackingContext.PRODUCT_DETAIL_PAGE,
    disabled: !isClientSide || !categorySlug,
    includeAgeLimitedByAlcohol: !!includeAlcohol,
    order: SortOrder.Desc,
    orderBy: SortKey.Score,
    slug: categorySlug || undefined,
    // limit +1 due to the result set potentially containing the already shown product
    limit: 13,
  })

  /**
   * Display loading indicators during SSR, or when client-side and either loading or haven't yet started to load when should be loaded
   */
  const displayLoadingIndicators = !isClientSide || (categorySlug && !called) || loading

  const relatedProducts = useMemo(
    () => data?.store?.products?.items?.filter((p) => p && p?.ean !== ean)?.slice(0, 12),
    [data?.store?.products?.items, ean],
  )

  const listName = `${LIST_NAME_PREFIX}: ${ean}`
  useProductsImpressionTracking({
    listName,
    trackingContext: TrackingContext.PRODUCT_DETAIL_PAGE,
    items: relatedProducts,
    ref,
    shouldTrackCategory: false,
  })
  /** Do not render anything when missing input */
  if (!ean || !categoryName || !categorySlug) {
    return null
  }

  /**
   * Do not render in case of error, or no related products found.
   */
  if (error || (!displayLoadingIndicators && relatedProducts?.length === 0)) {
    return null
  }

  const categoryLink = `/tuotteet/${categorySlug}?sort=${SORT_BY_SCORE}`

  return (
    <TrackingContextProvider
      metadata={{ listName }}
      trackingContext={TrackingContext.PRODUCT_DETAIL_PAGE}
    >
      <article className={className} data-test-id={LIST_NAME_PREFIX} ref={ref}>
        <StyledProductTitleWithCTA>
          <SectionTitle>{t`Top products`}</SectionTitle>
          <NextLink href={categoryLink}>{categoryName}</NextLink>
        </StyledProductTitleWithCTA>
        <TrackingContextProvider listName={listName}>
          <Carousel
            placeholderEans={displayLoadingIndicators ? PLACEHOLDERS : undefined}
            products={relatedProducts ?? []}
          />
        </TrackingContextProvider>
      </article>
    </TrackingContextProvider>
  )
}

export const TopProductsUpsell = styled(_TopProductsUpsell)(({ theme }) => ({
  marginTop: theme.spacings.large,
  position: 'relative',
}))
